import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DeclarationForm, SupervisorForms } from '.';
import { NOMINATION_TEXT, PAGE_COMMON_TEXT } from '../lang';
import { waitingForProcessing } from '../';
import {
  getApplicantInformation,
  useGetNominationBatch,
  useGetNominationId,
} from '../../../Nomination/graphql/NominationForm';
import {
  SideList,
  type ListSections,
  Loading,
  Alert,
  Accordion,
} from '../../../../components';
import {
  AdmissionRequirement,
  Fundings,
  type FundingSource,
  Remark,
} from '../../../Nomination/components';

const rightSidebarItems = [
  NOMINATION_TEXT.SUPERVISORS,
  NOMINATION_TEXT.GSB1_FORM.TITLE,
  NOMINATION_TEXT.FUNDING_SOURCE.TITLE,
  NOMINATION_TEXT.DECLARATION.TITLE,
  NOMINATION_TEXT.REMARKS.TITLE,
];

export const SupervisorNomination: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  /* lst means Activated List Item */
  let listItemActivated = searchParams.get('lst') ?? '';

  if (!rightSidebarItems.includes(listItemActivated)) {
    listItemActivated = NOMINATION_TEXT.SUPERVISORS;
  }

  const [clickedForm, setClickedForm] = useState<string>(listItemActivated);

  useEffect(() => {
    searchParams.set('lst', clickedForm);
    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [clickedForm]);

  const handleViewForm = (item: string): void => {
    setClickedForm(item);
  };

  const { nominationId } = useGetNominationId(
    {
      application: {
        _id: {
          eq: applicationId,
        },
      },
    },
    {},
  );

  const hasNominationId = Boolean(nominationId);

  /**
   * in monitor,
   * should not pass applicationId to API:getFundingSourceConfiguration
   * because monitor should display all related funding information even if any funding turns to disables
   */

  const {
    admission,
    supervisorTeam,
    fundings,
    declarations,
    remarks,
    departmentOptions,
    subjectClassification,
    allFundingSourceConfiguration,
    competitiveStudentship,
    loading: nominationBatchLoaing,
    error,
  } = useGetNominationBatch(nominationId); // this is nomination id

  const sources: FundingSource[] = fundings?.map(
    funding => funding.source as FundingSource,
  );
  const {
    applicationInfoData: { remarks: returnedRemarks },
    loading: applicationInfoDataLoading,
    error: applicationInfoDataError,
  } = getApplicantInformation(applicationId);
  const loading = nominationBatchLoaing || applicationInfoDataLoading;
  const { programme: programmeInGsb } = admission;

  const supervisors = supervisorTeam.map(supervisor => {
    const { confirmedAsCoSupervisor, ...rest } = supervisor;
    return {
      ...rest,
    };
  });

  const componentContainerMap: Record<string, React.ReactNode> = {
    [NOMINATION_TEXT.SUPERVISORS]: (
      <Accordion
        className="!mt-4"
        title={NOMINATION_TEXT.SUPERVISORS}
        disableExpand
        details={<SupervisorForms data={supervisors} />}
      />
    ),
    [NOMINATION_TEXT.GSB1_FORM.TITLE]: (
      <AdmissionRequirement
        isViewMode
        admission={admission}
        applicationId={applicationId}
        refetchAdmission={() => {}}
      />
    ),
    [NOMINATION_TEXT.FUNDING_SOURCE.TITLE]: (
      <Fundings
        isViewMode
        subjectClassification={subjectClassification}
        competitiveStudentship={competitiveStudentship}
        applicationId={applicationId}
        applicationNo={''} // applicationNo would not be in use in view mode
        programmeInGsb={programmeInGsb}
        departmentOptions={departmentOptions}
        fundingMapping={allFundingSourceConfiguration.mapping}
        sources={sources}
        fundings={fundings}
        refetchFunding={() => {}}
      />
    ),
    [NOMINATION_TEXT.DECLARATION.TITLE]: (
      <Accordion
        className="w-full !mt-[1rem]"
        title={NOMINATION_TEXT.DECLARATION.TITLE}
        defaultExpanded
        disableExpand
        details={
          <DeclarationForm
            data={declarations}
            supervisorsData={supervisorTeam}
            grayCheckbox={true}
          />
        }
      />
    ),
    [NOMINATION_TEXT.REMARKS.TITLE]: (
      <Remark
        isViewMode
        remarks={remarks}
        returnedRemarks={returnedRemarks}
        refetchRemark={() => {}}
      />
    ),
  };

  if (error ?? applicationInfoDataError) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (error as Error)?.message +
            (applicationInfoDataError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const listSections: ListSections[] = [
    {
      items: rightSidebarItems,
    },
  ];

  return (
    <>
      {loading ? (
        <Loading />
      ) : hasNominationId ? (
        <>
          <div className="grid grid-cols-[4fr,1fr]">
            <div>{componentContainerMap[clickedForm]}</div>
            <div className="mt-4">
              <SideList
                listSections={listSections}
                activatedForm={clickedForm}
                onChangeActivated={handleViewForm}
              />
            </div>
          </div>
        </>
      ) : (
        waitingForProcessing(
          PAGE_COMMON_TEXT.SIDEBAR_TEXT.SUPERVISOR_NOMINATION,
        )
      )}
    </>
  );
};
